/*
  Beeline cart: the free bee and the product forms (spec §3 "Free bee", §5 error handling).
  Loaded deferred from layout/theme.liquid, after Dawn's constants.js and pubsub.js.

  Bee rule: the product with handle `beeline-bee` belongs in the cart, quantity 1, while the
  subtotal of every other line is $45.00 or more (4500 cents, summed from final_line_price, i.e.
  cart.items_subtotal_price without the bee). Below that it is removed. Same rule as
  snippets/beeline-bee-progress.liquid and the Task 9 checkout validation Function.
  - Adds with /cart/add.js, removes and clamps with /cart/change.js (by line key).
  - Bee rows in the drawer and on /cart are locked: quantity input read-only (still submitted,
    Dawn posts updates[] by position), plus/minus disabled, remove button hidden.
  - Runs on page load when the cart has items and after every Dawn cart update (pubsub).
  - Failure: every bee progress line reveals its plain /cart link; a product form that cannot
    add by AJAX submits natively (POST /cart/add, then Shopify redirects to /cart).
    The Shopify Flow tag (Task 9) catches any order that still misses its bee.

  Product forms (form[data-bl-product-form], snippets/beeline-buy-form.liquid): AJAX add, then the
  Dawn cart drawer renders and opens; variant radios keep the CTA, price and URL in step.

  The decision functions are pure and exported for Node (scripts/beeline-cart.test.mjs).
*/
(function (factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && typeof document !== 'undefined') api.boot(window, document);
})(function () {
  'use strict';

  const BEE_HANDLE = 'beeline-bee';
  const THRESHOLD_CENTS = 4500;

  const lineCents = (item) => {
    const cents = item.final_line_price != null ? item.final_line_price : item.line_price;
    return Number(cents) || 0;
  };

  function isBeeLine(item, opts = {}) {
    if (!item) return false;
    const handle = opts.handle || BEE_HANDLE;
    if (item.handle === handle) return true;
    return opts.variantId != null && String(item.variant_id) === String(opts.variantId);
  }

  function subtotalExcludingBee(cart, opts = {}) {
    const items = (cart && cart.items) || [];
    return items.reduce((sum, item) => (isBeeLine(item, opts) ? sum : sum + lineCents(item)), 0);
  }

  /*
    Returns the cart operations that make the cart obey the bee rule, in order:
      { type: 'add', id, quantity: 1 }        POST /cart/add.js
      { type: 'change', key, quantity: n }     POST /cart/change.js (0 removes the line)
    An empty list means the cart is already right.
  */
  function decideBee(cart, opts = {}) {
    const threshold = opts.threshold != null ? Number(opts.threshold) : THRESHOLD_CENTS;
    const items = (cart && cart.items) || [];
    const bees = items.filter((item) => isBeeLine(item, opts));
    const subtotal = subtotalExcludingBee(cart, opts);
    const reached = subtotal >= threshold;
    const actions = [];

    if (!reached) {
      bees.forEach((item) => actions.push({ type: 'change', key: item.key, quantity: 0 }));
    } else if (bees.length === 0) {
      if (opts.variantId != null && opts.beeAvailable !== false) {
        actions.push({ type: 'add', id: opts.variantId, quantity: 1 });
      }
    } else {
      if (bees[0].quantity !== 1) actions.push({ type: 'change', key: bees[0].key, quantity: 1 });
      bees.slice(1).forEach((item) => actions.push({ type: 'change', key: item.key, quantity: 0 }));
    }

    return { subtotal, threshold, reached, remaining: Math.max(threshold - subtotal, 0), actions };
  }

  // "$14", "$14.50": no trailing zeros, like Liquid's money_without_trailing_zeros.
  function formatCents(cents, currency = 'USD', locale = 'en-US') {
    const whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(cents / 100);
    } catch (error) {
      return `$${whole ? cents / 100 : (cents / 100).toFixed(2)}`;
    }
  }

  function progressState(cart, opts = {}) {
    const decision = decideBee(cart, opts);
    const items = (cart && cart.items) || [];
    const beeInCart = items.some((item) => isBeeLine(item, opts));
    const percent = Math.min(100, Math.floor((decision.subtotal * 100) / decision.threshold));
    return {
      subtotal: decision.subtotal,
      remaining: decision.remaining,
      reached: decision.reached,
      beeInCart,
      percent,
    };
  }

  function boot(win, doc) {
    if (win.BeelineCart) return;
    win.BeelineCart = api;

    const routes = win.routes || {};
    const root = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
    const cartUrl = routes.cart_url || `${root}cart`;
    const addUrl = `${routes.cart_add_url || `${root}cart/add`}.js`;
    const changeUrl = `${routes.cart_change_url || `${root}cart/change`}.js`;
    const cartUpdateEvent =
      (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
    const canPublish = typeof publish === 'function';

    const request = (url, options = {}) =>
      fetch(url, {
        ...options,
        headers: { Accept: 'application/json', ...(options.headers || {}) },
      }).then((response) => {
        if (!response.ok) throw new Error(`${url} ${response.status}`);
        return response.json();
      });
    const postJSON = (url, body) =>
      request(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const getCart = () => request(`${cartUrl}.js`);

    // ---------- bee config ----------
    let config = null;
    const readConfig = () => {
      if (config) return Promise.resolve(config);
      const el = doc.querySelector('[data-bl-bee-progress]');
      const data = el ? el.dataset : {};
      const base = {
        handle: data.blBeeHandle || BEE_HANDLE,
        threshold: parseInt(data.blBeeThreshold, 10) || THRESHOLD_CENTS,
        variantId: data.blBeeVariant || null,
        beeAvailable: data.blBeeAvailable !== 'false',
      };
      if (base.variantId) {
        config = base;
        return Promise.resolve(config);
      }
      return request(`${root}products/${base.handle}.js`)
        .then((product) => {
          const variant = product.variants.find((v) => v.available) || product.variants[0];
          config = { ...base, variantId: variant ? variant.id : null, beeAvailable: Boolean(product.available) };
          return config;
        })
        .catch(() => {
          config = { ...base, beeAvailable: false };
          return config;
        });
    };

    // ---------- rendering ----------
    const lockBeeRows = () => {
      const handle = (config && config.handle) || BEE_HANDLE;
      const exact = new RegExp(`/products/${handle}(?:[?#/]|$)`);
      doc.querySelectorAll(`.cart-item a[href*="/products/${handle}"]`).forEach((link) => {
        if (!exact.test(link.getAttribute('href'))) return;
        const row = link.closest('.cart-item');
        if (!row || row.dataset.blBeeLocked) return;
        row.dataset.blBeeLocked = 'true';
        row.classList.add('bl-bee-row');
        row.querySelectorAll('.quantity__input').forEach((input) => {
          input.readOnly = true;
          input.max = '1';
          input.setAttribute('aria-readonly', 'true');
        });
        row.querySelectorAll('.quantity__button').forEach((button) => {
          button.disabled = true;
        });
        row.querySelectorAll('cart-remove-button').forEach((el) => {
          el.hidden = true;
        });
      });
    };

    const updateProgress = (cart) => {
      const state = progressState(cart, config || {});
      const currency = cart.currency || 'USD';
      doc.querySelectorAll('[data-bl-bee-progress]').forEach((el) => {
        if (el.closest('cart-drawer')) return; // the drawer is re-rendered from the server
        const text = el.querySelector('[data-bl-bee-text]');
        const fill = el.querySelector('[data-bl-bee-fill]');
        let message;
        if (!state.reached) {
          message = (el.dataset.blBeeLeft || '[amount] to the free bee').replace(
            '[amount]',
            formatCents(state.remaining, currency)
          );
        } else {
          message = state.beeInCart
            ? el.dataset.blBeeIn || 'Free bee: in your cart.'
            : el.dataset.blBeeDone || 'Free bee unlocked.';
        }
        if (text && message) text.textContent = message;
        if (fill) fill.style.setProperty('--bl-p', `${state.percent}%`);
        el.classList.toggle('is-reached', state.reached);
      });
    };

    const showFallback = (on) => {
      doc.querySelectorAll('[data-bl-bee-fallback]').forEach((link) => {
        link.hidden = !on;
      });
    };

    const htmlOf = (html, selector) => {
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return node ? node.innerHTML : null;
    };

    const renderSections = (cart) => {
      const targets = [];
      const drawer = doc.querySelector('cart-drawer');
      const drawerInner = drawer && drawer.querySelector('.drawer__inner');
      if (drawerInner) targets.push({ section: 'cart-drawer', selector: '.drawer__inner', el: drawerInner });
      const bubble = doc.getElementById('cart-icon-bubble');
      if (bubble) targets.push({ section: 'cart-icon-bubble', selector: '.shopify-section', el: bubble });
      ['main-cart-items', 'main-cart-footer'].forEach((id) => {
        const el = doc.getElementById(id);
        const contents = el && el.querySelector('.js-contents');
        if (el && el.dataset.id && contents) targets.push({ section: el.dataset.id, selector: '.js-contents', el: contents });
      });
      if (!targets.length) return Promise.resolve();

      const ids = targets.map((t) => t.section).join(',');
      return request(`${win.location.pathname}?sections=${encodeURIComponent(ids)}`).then((sections) => {
        targets.forEach((t) => {
          const html = sections[t.section] && htmlOf(sections[t.section], t.selector);
          if (html != null) t.el.innerHTML = html;
        });
        const empty = cart.item_count === 0;
        if (drawer) drawer.classList.toggle('is-empty', empty);
        doc.querySelectorAll('cart-items, cart-drawer-items').forEach((el) => el.classList.toggle('is-empty', empty));
        const footer = doc.getElementById('main-cart-footer');
        if (footer) footer.classList.toggle('is-empty', empty);
      });
    };

    // ---------- reconcile ----------
    const MAX_PASSES = 3;
    const run = () =>
      readConfig().then((cfg) =>
        getCart().then((cart) => {
          let changed = false;
          const pass = (current, n) => {
            const { actions } = decideBee(current, cfg);
            if (!actions.length || n >= MAX_PASSES) return Promise.resolve(current);
            changed = true;
            const steps = actions.reduce(
              (chain, action) =>
                chain.then(() =>
                  action.type === 'add'
                    ? postJSON(addUrl, { items: [{ id: Number(action.id), quantity: 1 }] }).catch((error) => {
                        cfg.beeAvailable = false; // sold out or unpublished: stop retrying this page view
                        throw error;
                      })
                    : postJSON(changeUrl, { id: action.key, quantity: action.quantity })
                ),
              Promise.resolve()
            );
            return steps.then(getCart).then((next) => pass(next, n + 1));
          };
          return pass(cart, 0).then((finalCart) => {
            updateProgress(finalCart);
            showFallback(false);
            if (!changed) return finalCart;
            return renderSections(finalCart).then(() => {
              if (canPublish) publish(cartUpdateEvent, { source: 'beeline-cart', cartData: finalCart });
              return finalCart;
            });
          });
        })
      );

    let running = null;
    let queued = false;
    const reconcile = () => {
      if (running) {
        queued = true;
        return running;
      }
      running = run()
        .catch((error) => {
          console.error('[beeline-cart]', error);
          showFallback(true);
        })
        .finally(() => {
          lockBeeRows();
          running = null;
          if (queued) {
            queued = false;
            reconcile();
          }
        });
      return running;
    };

    // ---------- product forms ----------
    const submitButtons = (form) => {
      const inside = Array.from(form.querySelectorAll('[type="submit"]'));
      const formId = form.getAttribute('id'); // form.id is shadowed by the input named "id"
      const outside = formId ? Array.from(doc.querySelectorAll(`[type="submit"][form="${CSS.escape(formId)}"]`)) : [];
      return inside.concat(outside);
    };

    const setCta = (form, available) => {
      submitButtons(form).forEach((button) => {
        button.disabled = !available;
        const label = button.querySelector('[data-bl-cta-label]');
        const text = available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
        if (label && text) label.textContent = text;
      });
    };

    const showFormError = (form, message) => {
      const box = form.querySelector('[data-bl-form-error]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    };

    const addFromForm = (form, submitter) => {
      const buttons = submitButtons(form);
      if (buttons.some((b) => b.disabled || b.getAttribute('aria-busy') === 'true')) return;
      showFormError(form, '');
      buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));

      const drawer = doc.querySelector('cart-drawer');
      const body = new FormData(form);
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.append('sections', drawer.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', win.location.pathname);
        if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(submitter || doc.activeElement);
      }

      fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            showFormError(form, response.description || response.message);
            return;
          }
          if (!drawer || typeof drawer.renderContents !== 'function') {
            win.location.href = cartUrl;
            return;
          }
          if (canPublish) {
            publish(cartUpdateEvent, { source: 'product-form', productVariantId: body.get('id'), cartData: response });
          } else {
            reconcile();
          }
          drawer.renderContents(response);
          drawer.classList.remove('is-empty');
        })
        .catch((error) => {
          console.error('[beeline-cart]', error);
          form.submit(); // native POST /cart/add; Shopify redirects to /cart
        })
        .finally(() => {
          buttons.forEach((b) => b.removeAttribute('aria-busy'));
        });
    };

    doc.addEventListener('submit', (event) => {
      const form = event.target.closest && event.target.closest('form[data-bl-product-form]');
      if (!form) return;
      event.preventDefault();
      addFromForm(form, event.submitter);
    });

    // Variant → cutout. The stage figure carries data-bl-cutouts = {"Blue": url, "Yellow": url, "Football": url}.
    // A variant title like "Blue · 7 cm" or "Football" picks its first segment.
    const swapStageCutout = (form, title) => {
      const scope = form.closest('.bl-pdp, .bl-land') || doc;
      const figure = scope.querySelector('[data-bl-cutouts]');
      if (!figure) return;
      let map;
      try { map = JSON.parse(figure.getAttribute('data-bl-cutouts')); } catch (e) { return; }
      const key = String(title).split(' · ')[0].split(' / ')[0].trim();
      const url = map && (map[key] || map[Object.keys(map).find((k) => key.toLowerCase().startsWith(k.toLowerCase())) || '']);
      if (!url) return;
      figure.querySelectorAll('img.bl-stk__img').forEach((img) => {
        if (img.dataset.blBase === url) return;
        img.dataset.blBase = url;
        img.srcset = [480, 720, 960, 1200].map((w) => `${url}?width=${w} ${w}w`).join(', ');
        img.src = `${url}?width=1200`;
      });
    };

    doc.addEventListener('change', (event) => {
      const radio = event.target.closest && event.target.closest('input[data-bl-variant]');
      if (!radio || !radio.checked || !radio.form) return;
      const form = radio.form;
      setCta(form, radio.dataset.available === 'true');
      doc.querySelectorAll(`[data-bl-price-for="${CSS.escape(form.getAttribute('id') || '')}"]`).forEach((el) => {
        el.textContent = radio.dataset.price;
      });
      swapStageCutout(form, radio.dataset.title || '');
      if (form.dataset.blUpdateUrl !== 'false' && win.history && win.history.replaceState) {
        const url = new URL(win.location.href);
        url.searchParams.set('variant', radio.value);
        win.history.replaceState({}, '', url.toString());
      }
    });

    // Multi-option forms (data-bl-option radios + hidden [data-bl-variant-id]): resolve the variant
    // from the picked values with the JSON table the snippet prints, then update id, CTA, price,
    // cutout and URL exactly like a single-option radio would.
    const resolveOptions = (form) => {
      const hidden = form.querySelector('input[data-bl-variant-id]');
      const table = form.querySelector('script[data-bl-variants]');
      if (!hidden || !table) return;
      let variants;
      try { variants = JSON.parse(table.textContent); } catch (e) { return; }
      const groups = form.querySelectorAll('[data-bl-option-group]');
      const picked = Array.from(groups).map((g) => {
        const r = g.querySelector('input[data-bl-option]:checked');
        return r ? r.value : null;
      });
      const match = variants.find((v) => v.options.every((o, i) => o === picked[i]));
      if (!match) { setCta(form, false); return; }
      hidden.value = match.id;
      setCta(form, !!match.available);
      doc.querySelectorAll(`[data-bl-price-for="${CSS.escape(form.getAttribute('id') || '')}"]`).forEach((el) => {
        el.textContent = match.price;
      });
      swapStageCutout(form, match.title || '');
      if (form.dataset.blUpdateUrl !== 'false' && win.history && win.history.replaceState) {
        const url = new URL(win.location.href);
        url.searchParams.set('variant', match.id);
        win.history.replaceState({}, '', url.toString());
      }
    };
    doc.addEventListener('change', (event) => {
      const radio = event.target.closest && event.target.closest('input[data-bl-option]');
      if (!radio || !radio.form) return;
      resolveOptions(radio.form);
    });

    // ---------- wiring ----------
    // Deep links (?variant=…) land with a non-default radio checked: show that variant's cutout on load.
    doc.querySelectorAll('form[data-bl-product-form]').forEach((form) => {
      const checked = form.querySelector('input[data-bl-variant]:checked');
      if (checked) swapStageCutout(form, checked.dataset.title || '');
    });
    if (typeof subscribe === 'function') {
      subscribe(cartUpdateEvent, (event) => {
        if (event && event.source === 'beeline-cart') return;
        lockBeeRows();
        reconcile();
      });
    }

    if (typeof MutationObserver === 'function') {
      let frame = 0;
      new MutationObserver(() => {
        if (frame) return;
        frame = win.requestAnimationFrame(() => {
          frame = 0;
          lockBeeRows();
        });
      }).observe(doc.body, { childList: true, subtree: true });
    }

    const start = () => {
      lockBeeRows();
      const hasItems = doc.querySelector('#cart-icon-bubble .cart-count-bubble, .cart-item');
      if (hasItems) reconcile();
    };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }

  const api = {
    BEE_HANDLE,
    THRESHOLD_CENTS,
    isBeeLine,
    subtotalExcludingBee,
    decideBee,
    formatCents,
    progressState,
    boot,
  };
  return api;
});
